import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { useProductSetup } from "@/hooks/useProductSetup";
import { useAeraWebSocket } from "@/hooks/useAeraWebSocket";
import { useMobileDetails } from "@/hooks/useMobileDetails";
import { apiFetch } from "@/lib/ApiFetch";
import { getSigninAuthTokenForApi } from "@/lib/signinPendingAuth";
import ProcessingIndicator from "./ProcessingIndicator";
import ScrollToBottomButton from "./ScrollToBottomButton";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { ChatInput } from "./chat/ChatInput";
import { SuggestedActions, type SuggestSection } from "./chat/SuggestedActions";

interface ChatMessage {
  id?: string;
  role: "user" | "ai";
  text: string;
  isStreaming?: boolean;
  isEnding?: boolean;
  apiPath?: string;
}

interface ChatPanelProps {
  workerName?: string;
  isHired?: boolean;
  totalTasks?: number;
  productId?: string;
  serviceId?: string;
  workspaceId?: string;
  userId?: string;
  sessionId?: string; // Unique session ID for each worker/chat
  taskId: string; // New prop for file uploads
  suggested_actions?: boolean;
  onContentScrollStateChange?: (isScrolled: boolean) => void;
}

type MarkdownLinkProps = ComponentPropsWithoutRef<"a"> & { node?: unknown };
type MarkdownImgProps = ComponentPropsWithoutRef<"img"> & { node?: unknown };

type WsMessage = {
  event?: "publish" | "stream" | string;
  data?: {
    request_id?: string;
    api_path?: string;
    text?: string;
    end?: boolean;
    responses?: Array<{ assistant_response?: string }>;
    agent_response?: string;
    response?: string;
    content?: string;
    [key: string]: unknown;
  };
};

type ChatHistoryApiMessage = {
  role?: string;
  text?: string;
  message?: string;
  content?: string;
};

// Simple icon component
const Icon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const icons: Record<string, React.ReactNode> = {
    link: (
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    ),
    file: (
      <>
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
    layout: (
      <>
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <line x1="3" x2="21" y1="9" y2="9" />
        <line x1="9" x2="9" y1="9" y2="21" />
      </>
    ),
    slack: (
      <>
        <rect width="3" height="3" x="13" y="2" rx="1.5" />
        <path d="M13 11a4 4 0 1 1-4-4" />
        <rect width="3" height="3" x="19" y="8" rx="1.5" />
        <path d="M19 8a4 4 0 1 1-4 4" />
        <rect width="3" height="3" x="8" y="19" rx="1.5" />
        <path d="M8 19a4 4 0 1 1 4-4" />
        <rect width="3" height="3" x="2" y="13" rx="1.5" />
        <path d="M2 13a4 4 0 1 1 4 4" />
      </>
    ),
    hourglass: (
      <>
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
        <path d="M7 22v-4.172a2 2 0 0 1 .586-1.414L12 12 7.586 7.586A2 2 0 0 1 7 6.172V2" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[name]}
    </svg>
  );
};

const parseMessage = (content: string) => {
  // Replace escaped newlines with actual newlines
  content = content.replace(/\\n/g, "\n");
  // Replace \n\n with \n + zero-width space + \n so remark-breaks produces <br><br>
  content = content.replace(/\n\n/g, "\n\u200B\n");
  // Escape "N)" at start of lines to prevent Markdown from creating <ol><li>
  content = content.replace(/(^|\n)(\d+)\) /g, "$1$2\\) ");
  const textLines: string[] = [];
  const attachmentLines: string[] = [];
  let suggestSection: SuggestSection | null = null;
  let hasLoadingSpinner = false;

  // Check for LOADING_SPINNER marker
  if (content.includes("[[LOADING_SPINNER]]")) {
    hasLoadingSpinner = true;
    content = content.replace(/\[\[LOADING_SPINNER\]\]/g, "").trim();
  }

  // Regex to match a line that contains ONLY markdown links [text](url)
  // Allowing multiple links on one line separated by spaces
  const linkLineRegex = /^(\s*\[[^\]]+\]\([^\)]+\)\s*)+$/;

  // Check for SUGGEST_SECTION - handles inline JSON with nested objects
  // Pattern: [[SUGGEST_SECTION]] followed by JSON object (can be multiline)
  const suggestMatch = content.match(/\[\[SUGGEST_SECTION\]\]([\s\S]*)$/);
  if (suggestMatch) {
    let jsonStr = suggestMatch[1].trim();

    // Remove the SUGGEST_SECTION from content so it doesn't show during streaming
    content = content.replace(/\[\[SUGGEST_SECTION\]\][\s\S]*$/, "").trim();
    // Remove trailing zero-width space sequences
    content = content.replace(/(\n\u200B|\n)+$/, "").trim();

    try {
      // Extract just the JSON block by finding the outermost braces
      const startIdx = jsonStr.indexOf("{");
      const endIdx = jsonStr.lastIndexOf("}");
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        jsonStr = jsonStr.substring(startIdx, endIdx + 1);
      }
      // Parse the JSON data
      suggestSection = JSON.parse(jsonStr) as SuggestSection;
    } catch {
      // Silently fail during streaming as the JSON string is often incomplete
    }
  }

  // Re-split after removing SUGGEST_SECTION
  const cleanedLines = content.split("\n");

  cleanedLines.forEach((line) => {
    // We only treat it as an attachment line if it matches the link pattern
    // AND it likely points to a file (optional check, but safer)
    if (linkLineRegex.test(line)) {
      attachmentLines.push(line);
    } else {
      textLines.push(line);
    }
  });

  return {
    text: textLines
      .join("\n")
      .replace(/(\n\u200B|\n)+$/, "")
      .trim(),
    attachments: attachmentLines.join("\n").trim(),
    suggestSection,
    hasLoadingSpinner,
  };
};

export default function ChatPanel({
  workerName = "AI Worker",
  isHired = false,
  productId,
  serviceId,
  workspaceId,
  userId,
  sessionId,
  taskId: _taskId, // New prop for file uploads (unused here)
  suggested_actions = true,
  onContentScrollStateChange,
}: ChatPanelProps) {
  void _taskId;
  void serviceId;
  void workspaceId;
  const { setShowMobileDetails } = useMobileDetails();
  const { setup } = useProductSetup(productId);
  const currentStep = setup?.currentStepIndex ?? 0;
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [, setHasNoHistory] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  const [showHireButton, setShowHireButton] = useState(true);
  const hasInitialScrolled = useRef(false);
  const isLoadingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingPublishRef = useRef<
    Record<string, { text: string; apiPath: string }>
  >({});
  const ownedRequestsRef = useRef<Map<string, "greeting" | "chat">>(new Map());
  const lastProcessedMsgKeyRef = useRef("");
  const streamThrottleRef = useRef<Record<string, { revealed: string }>>({});
  const [processingMessage, setProcessingMessage] = useState<string | null>(
    null,
  );

  const markdownComponents = useMemo<Components>(
    () => ({
      h1: "h1",
      h2: "h2",
      h3: "h3",
      h4: "h4",
      h5: "h5",
      h6: "h6",
      a: (props) => (
        <AeraButton
          as="a"
          {...(props as MarkdownLinkProps)}
          variant="link"
          textColor="amberdark"
          target="_blank"
          rel="noopener noreferrer"
        />
      ),
      img: (rawProps) => {
        const props = rawProps as MarkdownImgProps;
        if (props.alt === "cursor") {
          return (
            <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "0ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "150ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "300ms", animationDuration: "1s" }}
              ></span>
            </span>
          );
        }
        return (
          <AeraImage
            native
            src={typeof props.src === "string" ? props.src : ""}
            alt={props.alt ?? ""}
            className="max-w-full rounded-lg"
          />
        );
      },
    }),
    [],
  );

  // WebSocket Integration
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;

  // Determine user ID for room subscription
  const getUserId = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user.userId;
      }
    } catch {}
    // Fallback to prop or demo
    return userId || "demo";
  };

  const currentUserId = getUserId();
  const roomId = `user:${currentUserId}`;

  // If WS stream/publish arrives BEFORE we register request_id (e.g. greeting), queue it here and flush later.
  const pendingWsByRequestIdRef = useRef<
    Map<
      string,
      {
        streamText?: string;
        isEnd?: boolean;
        publishText?: string;
        apiPath?: string;
      }
    >
  >(new Map());

  const flushPendingForRequestId = useCallback((requestId: string) => {
    const pending = pendingWsByRequestIdRef.current.get(requestId);
    if (!pending) return;

    // 1) Apply pending stream (shows cursor/dots)
    if (typeof pending.streamText === "string") {
      setChatHistory((prev) => {
        if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
        const idx = prev.findIndex(
          (m) => m.id === requestId && m.role === "ai",
        );
        if (idx !== -1) {
          return [
            ...prev.slice(0, idx),
            {
              ...prev[idx],
              text: pending.streamText ?? "",
              isStreaming: true,
              isEnding: !!pending.isEnd,
            },
            ...prev.slice(idx + 1),
          ];
        }
        return [
          ...prev,
          {
            role: "ai" as const,
            text: pending.streamText ?? "",
            id: requestId,
            isStreaming: true,
            isEnding: !!pending.isEnd,
          },
        ];
      });
    }

    // 2) Apply pending publish (final answer)
    const finalPublishText =
      typeof pending.publishText === "string" ? pending.publishText : "";
    if (finalPublishText) {
      setChatHistory((prev) => {
        const filtered = prev.filter(
          (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
        );
        if (
          filtered.some((m) => m.text === finalPublishText && m.role === "ai")
        )
          return filtered;
        return [
          ...filtered,
          {
            role: "ai",
            text: finalPublishText,
            id: `${requestId}-publish`,
            isStreaming: false,
            apiPath: pending.apiPath,
          },
        ];
      });
      setIsTyping(false);
      setProcessingMessage(null);
      ownedRequestsRef.current.delete(requestId);
    }

    pendingWsByRequestIdRef.current.delete(requestId);
  }, []);

  const {} = useAeraWebSocket({
    roomId,
    token,
    // Match TestingChatPanel: always enable when roomId is defined; token is optional.
    enabled: !!roomId,
    onMessage: (msg: unknown) => {
      if (!msg) return;
      const data = msg as WsMessage;

      // Dedup: skip exact duplicate
      const msgKey = `${data.event}:${data.data?.request_id}:${data.data?.text}:${data.data?.end}`;
      if (msgKey === lastProcessedMsgKeyRef.current) return;
      lastProcessedMsgKeyRef.current = msgKey;

      if (data.event === "publish" && data.data) {
        const pubText =
          data.data.responses?.[0]?.assistant_response ||
          data.data.agent_response ||
          data.data.text ||
          data.data.response ||
          data.data.content;
        const requestId = data.data.request_id;
        const apiPath = data.data.api_path;

        // Only handle request_ids owned by this component (เหมือน AgentChatPanel)
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(pendingWsByRequestIdRef.current.get(requestId) || {}),
            publishText:
              typeof pubText === "string"
                ? pubText
                : JSON.stringify(pubText ?? ""),
            apiPath,
          });
          return;
        }

        if (pubText) {
          setChatHistory((prev) => {
            // publish = final answer. Always replace stream bubble immediately (match AgentChatPanel behavior).
            const filtered = prev.filter(
              (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
            );
            if (filtered.some((m) => m.text === pubText && m.role === "ai"))
              return filtered;
            return [
              ...filtered,
              {
                role: "ai",
                text: pubText,
                id: `${requestId}-publish`,
                isStreaming: false,
                apiPath,
              },
            ];
          });
        }
        setIsTyping(false);
        setProcessingMessage(null);
        ownedRequestsRef.current.delete(requestId);
      } else if (data.event === "stream" && data.data) {
        const socketText = data.data.text ?? "";
        const requestId = data.data.request_id;
        const isEnd = data.data.end === true;

        // Only handle request_ids owned by this component (เหมือน AgentChatPanel)
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          const prevPending = pendingWsByRequestIdRef.current.get(requestId);
          const nextStreamText =
            (prevPending?.streamText ?? "") + (socketText ?? "");
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(prevPending || {}),
            streamText: nextStreamText,
            isEnd,
          });
          return;
        }

        if (!streamThrottleRef.current[requestId]) {
          streamThrottleRef.current[requestId] = { revealed: "" };
        }
        const tState = streamThrottleRef.current[requestId];

        // Match AgentChatPanel behavior: even if socketText is "", still mark streaming so UI indicators render.
        tState.revealed = tState.revealed + socketText;
        const displayText = tState.revealed;
        setChatHistory((prev) => {
          if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
          const idx = prev.findIndex(
            (m) => m.id === requestId && m.role === "ai",
          );
          if (idx !== -1) {
            return [
              ...prev.slice(0, idx),
              {
                ...prev[idx],
                text: displayText,
                isStreaming: true,
                isEnding: isEnd,
              },
              ...prev.slice(idx + 1),
            ];
          }
          return [
            ...prev,
            {
              role: "ai" as const,
              text: displayText,
              id: requestId,
              isStreaming: true,
              isEnding: isEnd,
            },
          ];
        });
        setIsTyping(false);

        if (isEnd) {
          const pub = pendingPublishRef.current[requestId];
          delete streamThrottleRef.current[requestId];
          setIsTyping(false);
          setProcessingMessage(null);

          if (pub) {
            delete pendingPublishRef.current[requestId];
            setChatHistory((curr) => {
              const filtered = curr.filter(
                (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
              );
              if (
                !filtered.some((m) => m.text === pub.text && m.role === "ai")
              ) {
                filtered.push({
                  role: "ai",
                  text: pub.text,
                  id: `${requestId}-publish`,
                  isStreaming: false,
                  apiPath: pub.apiPath,
                });
              }
              return [...filtered];
            });
          } else {
            // Match AgentChatPanel: don't force isStreaming false on end; wait for publish to finalize.
          }
        }
      }
    },
  });

  // Load chat history from API
  useEffect(() => {
    async function loadChatHistory() {
      if (isLoadingRef.current) return; // Prevent duplicate calls

      isLoadingRef.current = true;
      setIsLoadingHistory(true);

      // Only load history if we have an explicit session ID
      // Do not fall back to productId as that causes 403 errors (product ID != session ID)
      const currentSessionId = sessionId;

      if (!currentSessionId) {
        // Wait for session ID to be available from parent
        isLoadingRef.current = false;
        setIsLoadingHistory(false);
        setIsTyping(false);
        return;
      }

      try {
        const chatApiUrl = process.env.NEXT_PUBLIC_CHAT_API_URL;
        const token = getSigninAuthTokenForApi() || "";

        if (!chatApiUrl) {
          throw new Error("Chat API URL not configured");
        }

        // Use product-based API endpoint format for history: /v1/chats/history/{product_id}
        const fetchGreeting = async () => {
          try {
            // Greeting (first message) loading indicator — avoid default "is thinking" copy.
            setProcessingMessage("กำลังโหลดประวัติแชท");
            setIsTyping(true);
            const greetingResponse = await fetch("/api/proxy", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                url: `${chatApiUrl}/v1/chat/greeting`,
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                data: {
                  session_id: currentSessionId,
                  product_id: productId,
                  language: "ไทย",
                  suggested_actions: suggested_actions,
                },
              }),
            });

            if (greetingResponse.ok) {
              const greetingData = await greetingResponse.json();
              // Register request_id so WS onMessage can route to this component
              if (greetingData.request_id) {
                ownedRequestsRef.current.set(
                  greetingData.request_id,
                  "greeting",
                );
                flushPendingForRequestId(greetingData.request_id);
              }
              const greetingText =
                greetingData.response ||
                greetingData.message ||
                greetingData.text;

              // If the response is just a queue acknowledgment, don't show it. The websocket will stream the real message.
              if (
                greetingText &&
                !greetingText.toLowerCase().includes("queued for processing")
              ) {
                setChatHistory([{ role: "ai", text: greetingText }]);
                setHasNoHistory(false);
                setProcessingMessage(null);
                setIsTyping(false);
              } else if (
                greetingText?.toLowerCase().includes("queued for processing")
              ) {
                // Empty out history so the stream can start fresh
                setChatHistory([]);
                setHasNoHistory(false);
                // Keep indicator on since stream is coming
                setIsTyping(true);
              } else {
                setChatHistory([]);
                setHasNoHistory(true);
                setProcessingMessage(null);
                setIsTyping(false);
              }
            } else {
              console.warn("Greeting API failed:", greetingResponse.status);
              setChatHistory([]);
              setHasNoHistory(true);
              setProcessingMessage(null);
              setIsTyping(false);
            }
          } catch (greetingError) {
            console.warn("Error fetching greeting:", greetingError);
            setChatHistory([]);
            setHasNoHistory(true);
            setProcessingMessage(null);
            setIsTyping(false);
          }
        };

        // Hire preview has no seller session yet — GET history returns 403
        // "don't have access to this session". Skip straight to greeting.
        if (!isHired) {
          await fetchGreeting();
          return;
        }

        const historyUrl = `${chatApiUrl}/v1/chats/history/${encodeURIComponent(currentSessionId)}`;
        const response = await fetch("/api/proxy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: historyUrl,
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        });

        if (response.status === 403) {
          let detail = "";
          try {
            const errBody = (await response.clone().json()) as {
              detail?: string;
            };
            detail = errBody.detail || "";
          } catch {
            /* ignore */
          }
          console.warn(
            "[Chat] History 403 — session key ไม่ตรงกับผู้ใช้ใน token หรือยังไม่มีสิทธิ์:",
            detail || response.statusText,
            { sessionId: currentSessionId, url: historyUrl },
          );
        }

        if (response.ok) {
          const data = await response.json();
          // Transform API response to ChatMessage format
          // API returns: {status: "success", messages: [...]}
          if (data.messages && Array.isArray(data.messages)) {
            const loadedHistory: ChatMessage[] = (
              data.messages as ChatHistoryApiMessage[]
            ).map((msg) => ({
              role: msg.role === "user" ? "user" : "ai",
              text: msg.text || msg.message || msg.content || "",
            }));

            if (loadedHistory.length > 0) {
              // Reverse array so oldest messages are at the top, newest at the bottom
              setChatHistory(loadedHistory.reverse());
              setHasNoHistory(false);
            } else {
              // No history found, try calling greeting API
              await fetchGreeting();
            }
          } else {
            // Invalid response format
            await fetchGreeting();
          }
        } else {
          // API error or no history found
          await fetchGreeting();
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        // Show no history message on error
        setChatHistory([]);
        setHasNoHistory(true);
      } finally {
        setIsLoadingHistory(false);
        isLoadingRef.current = false;
      }
    }

    loadChatHistory();
  }, [sessionId, suggested_actions, productId, flushPendingForRequestId, isHired]);

  const handleScroll = useCallback(() => {
    const container = chatScrollRef.current;
    if (!container) return;

    onContentScrollStateChange?.(container.scrollTop > 0);

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 150);

    const currentScrollTop = container.scrollTop;

    // Only trigger if scroll difference is significant to avoid flickering
    if (
      Math.abs(currentScrollTop - lastScrollTopRef.current) > 10 ||
      currentScrollTop < 10
    ) {
      if (currentScrollTop > lastScrollTopRef.current) {
        // Scrolling down -> show button
        setShowHireButton(true);
      } else if (currentScrollTop < lastScrollTopRef.current) {
        // Scrolling up -> hide button
        setShowHireButton(false);
      }
    }

    lastScrollTopRef.current = currentScrollTop;
  }, [onContentScrollStateChange]);

  const scrollToBottom = useCallback(() => {
    const container = chatScrollRef.current;
    if (container)
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, []);

  // Auto-scroll: always jump to bottom on initial load; smart 150px threshold after
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!chatHistory.length) return;
      const container = chatScrollRef.current;
      if (!container) return;
      if (!hasInitialScrolled.current) {
        container.scrollTop = container.scrollHeight;
        hasInitialScrolled.current = true;
        return;
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom <= 150) {
        container.scrollTop = container.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [chatHistory, isTyping, processingMessage]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text) return;

    setIsTyping(true);

    try {
      const newHistory: ChatMessage[] = [
        ...chatHistory,
        { role: "user", text },
      ];
      setChatHistory(newHistory);
      requestAnimationFrame(() => scrollToBottom());
      setInputValue("");
      setHasNoHistory(false);

      const chatApiUrl = process.env.NEXT_PUBLIC_CHAT_API_URL;
      if (!chatApiUrl || !sessionId) {
        throw new Error("Chat API URL or Session ID not configured");
      }

      const data = await apiFetch<unknown>(
        `${chatApiUrl}/v1/chat/product/${productId}`,
        {
          method: "POST",
          body: JSON.stringify({
            message: text,
            session_id: sessionId,
            suggested_actions: suggested_actions,
          }),
        },
      );

      if (
        typeof data === "object" &&
        data !== null &&
        (data as { status?: unknown }).status === "accepted" &&
        typeof (data as { request_id?: unknown }).request_id === "string"
      ) {
        // Queued — WS will stream the response; register request_id
        const requestId = (data as { request_id: string }).request_id;
        ownedRequestsRef.current.set(requestId, "chat");
        flushPendingForRequestId(requestId);
      } else {
        // Synchronous response — show directly
        const obj =
          typeof data === "object" && data !== null
            ? (data as Record<string, unknown>)
            : null;
        const aiResponse =
          (typeof obj?.response === "string" && obj.response) ||
          (typeof obj?.message === "string" && obj.message) ||
          (typeof obj?.text === "string" && obj.text) ||
          null;
        if (aiResponse) {
          setChatHistory((prev) => [...prev, { role: "ai", text: aiResponse }]);
        }
        setIsTyping(false);
      }
    } catch (error) {
      console.error("Error in send flow:", error);
      setChatHistory((prev) => [
        ...prev,
        {
          role: "ai",
          text: "Sorry, I encountered an error. Please try again later.",
        },
      ]);
      setIsTyping(false);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Chat Area */}
        <div className="flex-1 relative overflow-hidden">
          <div
            ref={chatScrollRef}
            className="absolute inset-0 p-4 overflow-y-auto space-y-4"
            onScroll={handleScroll}
          >
            <>
              {chatHistory.map((msg, i) => {
                const { text, attachments, suggestSection } = parseMessage(
                  msg.text,
                );
                const isUser = msg.role === "user";

                return (
                  <div key={i}>
                    <div
                      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 duration-300`}
                    >
                      <div
                        className={`flex flex-col max-w-[80%] ${isUser ? "items-end" : "items-start"} ${!isUser && msg.isEnding ? "animate-pulse" : ""}`}
                      >
                        {/* Attachments - Outside Bubble (Top) */}
                        {attachments && (
                          <div
                            className={`mb-1 flex flex-col ${isUser ? "items-end text-right" : "items-start text-left"} w-full`}
                          >
                            {/* <div className={`${isUser ? "text-right" : "text-left"}`}> */}
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm, remarkBreaks]}
                              components={{
                                a: (rawProps) => {
                                  const props = rawProps as MarkdownLinkProps;
                                  const content = String(props.children);
                                  const href = props.href || "";
                                  const isFile =
                                    href.match(
                                      /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|jpg|jpeg|png|gif|webp)$/i,
                                    ) || href.includes("/uploads/documents/");

                                  if (isFile) {
                                    const fileName = content;
                                    let cleanHref = href;
                                    try {
                                      cleanHref = decodeURIComponent(href);
                                    } catch {
                                      // ignore error
                                    }
                                    const fileType =
                                      cleanHref
                                        .split("?")[0]
                                        .split(".")
                                        .pop()
                                        ?.toUpperCase() || "FILE";
                                    const isImage = [
                                      "JPG",
                                      "JPEG",
                                      "PNG",
                                      "GIF",
                                      "WEBP",
                                      "SVG",
                                      "BMP",
                                    ].includes(fileType);

                                    if (isImage) {
                                      return (
                                        <AeraImage
                                          native
                                          src={href}
                                          alt={fileName}
                                          className="max-w-full rounded-lg my-2 cursor-pointer hover:opacity-90 transition-opacity border border-gray-200"
                                          style={{ maxHeight: "300px" }}
                                        />
                                      );
                                    }

                                    return (
                                      <div
                                        className="flex items-center gap-3 p-3 bg-aera-white border border-aera-lightgray/80 rounded-xl my-2 cursor-pointer hover:bg-aera-lightgray transition-colors max-w-sm group shadow-sm text-left"
                                        onClick={() =>
                                          window.open(href, "_blank")
                                        }
                                      >
                                        <div className="bg-aera-redlight p-2 rounded-lg text-aera-red">
                                          <Icon name="file" size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-aera-charcoal truncate text-sm">
                                            {fileName}
                                          </div>
                                          <div className="text-xs text-aera-charcoal">
                                            {fileType}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <AeraButton
                                      as="a"
                                      {...props}
                                      variant="link"
                                      textColor="darkamber"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {content}
                                    </AeraButton>
                                  );
                                },
                              }}
                            >
                              {attachments}
                            </ReactMarkdown>
                            {/* </div> */}
                          </div>
                        )}

                        {/* Text Bubble (Bottom) */}
                        {(text || msg.isStreaming) && (
                          <div
                            className={`px-3 opacity-90 transition-opacity rounded-lg text-sm ${
                              isUser
                                ? "bg-aera-lightgray text-aera-charcoal rounded-br-none py-1"
                                : "bg-aera-white text-aera-charcoal border border-aera-lightgray/80 rounded-bl-none py-3"
                            }`}
                            style={{
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                            }}
                          >
                            {msg.role === "ai" && (
                              <div className="text-xs font-semibold text-aera-charcoal mb-1">
                                {workerName}
                              </div>
                            )}
                            {msg.role === "ai" ? (
                              <div
                                className="prose prose-sm max-w-none prose-slate
                                  prose-headings:text-aera-charcoal prose-headings:font-bold prose-headings:mb-1 prose-headings:mt-2
                                  prose-h2:text-lg prose-h3:text-base prose-h4:text-sm
                                  prose-p:leading-relaxed prose-p:my-1 prose-p:mb-2
                                  prose-strong:text-aera-charcoal prose-strong:font-bold
                                  prose-ul:list-disc prose-ul:pl-4 prose-ul:my-1 prose-ul:space-y-1
                                  prose-ol:list-decimal prose-ol:pl-4 prose-ol:my-1 prose-ol:space-y-1
                                  prose-li:my-0.5
                                  prose-code:bg-aera-white prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-aera-charcoal prose-code:text-[13px] prose-code:before:content-none prose-code:after:content-none
                                  prose-pre:bg-aera-charcoal prose-pre:text-aera-white prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                                  prose-a:text-aera-amberdark prose-a:no-underline hover:prose-a:underline
                                  prose-blockquote:border-l-4 prose-blockquote:border-aera-amber prose-blockquote:pl-4 prose-blockquote:italic
                                  prose-hr:my-4 prose-hr:border-gray-200
                                  wrap-break-word"
                              >
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={markdownComponents}
                                >
                                  {msg.isStreaming
                                    ? `${text} ![cursor](cursor)`
                                    : text}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              <div className="prose prose-sm max-w-none prose-invert text-aera-charcoal prose-p:my-0 prose-pre:my-0 prose-a:text-aera-amberdark">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={{
                                    a: (rawProps) => {
                                      const props =
                                        rawProps as MarkdownLinkProps;
                                      return (
                                        <AeraButton
                                          as="a"
                                          {...props}
                                          variant="link"
                                          textColor="amberdark"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        />
                                      );
                                    },
                                  }}
                                >
                                  {text}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Suggested Actions - Below Text Bubble */}
                        <SuggestedActions
                          suggestSection={suggestSection}
                          isUser={isUser}
                          disabled={isTyping}
                          onSelect={(label) => handleSendMessage(label)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {(isTyping || isLoadingHistory) && (
                <ProcessingIndicator
                  workerName={workerName}
                  processingMessage={
                    isLoadingHistory ? "กำลังโหลดประวัติแชท" : processingMessage
                  }
                />
              )}
            </>

            <div ref={chatEndRef} />
          </div>
          {/* Scroll to bottom button */}
          {showScrollButton && (
            <ScrollToBottomButton onClick={scrollToBottom} />
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 flex flex-col gap-3">
          {/* Mobile Hire Button */}
          {
            <div
              className={`xl:hidden transition-all duration-300 ease-in-out ${showHireButton ? "max-h-16 opacity-100" : "max-h-0 opacity-0 -mb-3"}`}
            >
              <AeraButton
                variant="primary"
                fontWeight="medium"
                fullWidth
                onClick={() => setShowMobileDetails(true)}
                disabled={isTyping || isLoadingHistory}
              >
                จ้าง Worker
              </AeraButton>
            </div>
          }

          <ChatInput
            textareaRef={textareaRef}
            value={inputValue}
            onChange={setInputValue}
            onSend={() => handleSendMessage()}
            placeholder={
              !isHired
                ? `สัมภาษณ์ ${workerName}...`
                : currentStep === 0
                  ? "วางลิงก์สำหรับเทรน..."
                  : "พิมพ์คำสั่งของคุณ..."
            }
            disabled={isTyping || isLoadingHistory}
            sendDisabled={
              !inputValue.trim() || isTyping || isLoadingHistory
            }
          />
        </div>
      </div>
    </>
  );
}
